import { apiGet } from "@/shared/integrations/api/client";
import { supabase } from "@/shared/integrations/supabase/client";
import { isDemoUser } from "@/shared/lib/userIdUtils";
import { isAdminUser } from "@/shared/lib/adminUids";
import { firebaseAuth } from "@/shared/integrations/firebase/client";
import { resolveClientId } from "@/shared/lib/resolveClientId";

/** Chunk ref_id arrays to keep Supabase IN() queries within URL length limits. */
const REF_ID_CHUNK = 200;
const chunk = <T,>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

/**
 * Response shape of GET /projects/client (subset we use). The endpoint scopes
 * results to the caller's organization via the Firebase token, so no org id is
 * passed. With `returnIdsOnly=true` it returns `projectIds` — Firebase project
 * ids, which equal Supabase `properties.ref_id`.
 */
type ClientProjectQueryResult = {
  projects?: unknown[];
  projectIds?: string[];
  total: number;
  totalRevenue?: number;
};

/**
 * Fetch the authorized property ref_ids for the current user's org.
 *
 * Returns Firebase project ids (== Supabase `properties.ref_id`). The app then
 * loads those properties from Supabase by `ref_id`. A high `limit` is used to
 * retrieve the full authorized set in one call (the client portal lists are not
 * paginated for a single org's dashboard).
 */
// Short-lived cache so a single render burst (table count + page + stage counts,
// or multiple hooks) doesn't re-hit the endpoint for the same authorized set.
let _refIdsCache: { at: number; promise: Promise<string[]> } | null = null;
const REF_IDS_CACHE_TTL_MS = 15_000;

export function fetchAuthorizedPropertyRefIds(): Promise<string[]> {
  const now = Date.now();
  if (_refIdsCache && now - _refIdsCache.at < REF_IDS_CACHE_TTL_MS) {
    return _refIdsCache.promise;
  }
  // The demo account has no Firebase org, so /projects/client returns nothing.
  // Read the demo org's property ref_ids straight from Supabase instead.
  const promise = isDemoUser()
    ? fetchDemoPropertyRefIds()
    : apiGet<ClientProjectQueryResult>("/projects/client", {
        returnIdsOnly: true,
        limit: 10000,
      }).then((result) => result.projectIds ?? []);
  _refIdsCache = { at: now, promise };
  return promise;
}

/** Demo-only: the authorized ref_ids are simply the demo org's properties,
 *  read directly from Supabase (the backend endpoint can't see a Firebase-less
 *  org). Empty when the claim hasn't resolved a client yet. */
async function fetchDemoPropertyRefIds(): Promise<string[]> {
  const clientId = await resolveClientId();
  if (!clientId) return [];
  const { data, error } = await (supabase as any)
    .from("properties")
    .select("ref_id")
    .eq("organization_id", clientId);
  if (error) throw error;
  return (data ?? [])
    .map((r: { ref_id: string | null }) => r.ref_id)
    .filter((id: string | null): id is string => !!id);
}

/**
 * Paged/search variant: returns one page of authorized property ref_ids plus the
 * total match count, with search/pagination handled by the backend. Used by the
 * paginated property list (hydrated from Supabase by ref_id).
 */
export async function queryAuthorizedPropertyRefIds(params: {
  query?: string;
  limit: number;
  offset?: number;
}): Promise<{ refIds: string[]; total: number }> {
  const result = await apiGet<ClientProjectQueryResult>("/projects/client", {
    returnIdsOnly: true,
    query: params.query && params.query.trim() ? params.query : undefined,
    limit: params.limit,
    offset: params.offset,
  });
  return { refIds: result.projectIds ?? [], total: result.total ?? 0 };
}

/**
 * Fetch the authorized properties for the current user's org from Supabase,
 * scoped by the ref_id list from {@link fetchAuthorizedPropertyRefIds}.
 *
 * Replaces the legacy `.from('properties').eq('organization_id', clientId)`
 * pattern: the backend (not RLS or an org filter) decides which properties the
 * caller may see. `select` is the Supabase column projection string; child rows
 * (assets, documents) are still keyed off the returned `property.id`.
 */
/**
 * Fetch a specific org's properties by organization_id (the admin / cross-client
 * path — admins switch between clients, so they query by the selected org rather
 * than the token-scoped endpoint). Relies on the organization_id linkage.
 */
export async function fetchPropertiesByOrgId<T = Record<string, unknown>>(
  orgId: string,
  select: string,
  refine?: (query: any) => any,
): Promise<T[]> {
  const rows: T[] = [];
  const pageSize = 1000;
  let from = 0;
  while (true) {
    let query = (supabase as any)
      .from("properties")
      .select(select)
      .eq("organization_id", orgId);
    if (refine) query = refine(query);
    const { data, error } = await query.range(from, from + pageSize - 1);
    if (error) throw error;
    if (data?.length) rows.push(...(data as T[]));
    if (!data || data.length < pageSize) break;
    from += pageSize;
  }
  return rows;
}

/**
 * Properties for an explicitly named client (Supabase `projects.id`), admin-aware.
 *
 * Exports pass the *selected* client id (the sidebar switcher's org for admins,
 * the user's own org for client users). The token-scoped
 * {@link fetchAuthorizedProperties} can't serve admins: it derives scope from the
 * caller's own `organization_id` claim, so a cross-client admin gets the wrong
 * org — and since ENG-320 pinned that path to `organization_id`, an empty set.
 * Branch exactly like the client asset hooks (useClientAssets):
 * - Admins: the selected client by organization_id (cross-client switching).
 * - Client users: their token/team-scoped authorized set.
 */
export function fetchClientProperties<T = Record<string, unknown>>(
  projectId: string,
  select: string,
  refine?: (query: any) => any,
): Promise<T[]> {
  return isAdminUser(firebaseAuth?.currentUser?.uid)
    ? fetchPropertiesByOrgId<T>(projectId, select, refine)
    : fetchAuthorizedProperties<T>(select, refine);
}

export async function fetchAuthorizedProperties<T = Record<string, unknown>>(
  select: string,
  refine?: (query: any) => any,
): Promise<T[]> {
  const clientId = await resolveClientId();
  if (!clientId) return [];

  // Demo: no Firebase org → read the demo org's properties directly by org id
  // (same shape as the admin cross-client path) instead of via the backend.
  if (isDemoUser()) {
    return fetchPropertiesByOrgId<T>(clientId, select, refine);
  }

  const refIds = await fetchAuthorizedPropertyRefIds();
  const rows: T[] = [];
  for (const idChunk of chunk(refIds, REF_ID_CHUNK)) {
    // Pin to the caller's org: the authorized ref_id set can resolve to
    // properties across orgs (unsynced duplicate rows share a ref_id), so the
    // ref_id list alone over-counts. organization_id is the authoritative client
    // link and matches the properties page (clientPropertiesQueries).
    let query = (supabase as any)
      .from("properties")
      .select(select)
      .in("ref_id", idChunk)
      .eq("organization_id", clientId);
    if (refine) query = refine(query);
    const { data, error } = await query;
    if (error) throw error;
    if (data?.length) rows.push(...(data as T[]));
  }
  return rows;
}
